import React, { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
	deleteImportBatch,
	dismissImportError,
	fetchFromMonarch,
	loadImportBatches,
	selectImportBatch,
	uploadImportFile,
} from '../../../redux/actions/import.js';
import { settingsTabChange } from '../../../redux/actions/settings.js';
import importBatchName from '../../../utilities/importBatchName.js';
import monarchBalanceSummary from '../../../utilities/monarchBalanceSummary.js';
import Button from '../../components/Button.jsx';
import Card from '../../components/Card.jsx';
import CircleLoader from '../../components/CircleLoader.jsx';
import ImportAccountMapper from './ImportAccountMapper.jsx';
import ImportBalanceReport from './ImportBalanceReport.jsx';
import ImportCategoryMapper from './ImportCategoryMapper.jsx';
import ImportPreviewTable from './ImportPreviewTable.jsx';
import { IMPORT_RULES_TAB } from './ImportRules.jsx';
import MonarchConnection from './MonarchConnection.jsx';

/**
 * Uploading and mapping an export.
 *
 * Two kinds of file land here, told apart by their own headers rather than by asking: a transaction
 * export, which is staged and previewed and stops short of writing anything, and a balance export,
 * which backfills daily history and checks the transaction import against itself.
 */
export default function ImportSettings() {
	const dispatch = useDispatch();
	const fileInput = useRef(null);

	const batches = useSelector((state) => state.importing.batches);
	const selectedBatch = useSelector((state) => state.importing.selectedBatch);
	const uploading = useSelector((state) => state.importing.uploading);
	const fetching = useSelector((state) => state.importing.fetching);
	const error = useSelector((state) => state.importing.error);
	const balances = monarchBalanceSummary(useSelector((state) => state.importing.monarchBalances));

	/** Again on a budget switch, which forgets the last budget's Monarch connection. */
	const budgetId = useSelector((state) => {
		const budget = state.dashboard && state.dashboard.player && state.dashboard.player.active_budget;
		return budget ? budget.id : null;
	});
	useEffect(() => {
		dispatch(loadImportBatches());
	}, [budgetId]);

	const onFileChosen = (event) => {
		const file = event.target.files && event.target.files[0];
		if(!file) {
			return;
		}
		dispatch(uploadImportFile(file))
			.then(() => dispatch(loadImportBatches()));
		/** Let the same file be picked again after a failed parse. */
		event.target.value = '';
	};

	const onFetchFromMonarch = () => {
		dispatch(fetchFromMonarch())
			.then(() => dispatch(loadImportBatches()));
	};

	/**
	 * Deleting takes the file and its staged rows. Two things it does not take are worth saying
	 * outright: the mappings it produced are budget-scoped and stay behind on purpose, or this reads
	 * like it undoes the whole session -- and a partly-imported file has already written rows into
	 * the ledger, which deleting the upload does not reach. A batch only reaches 'imported' once
	 * nothing in it is blocked, so one that is still deletable may nonetheless have imported a good
	 * deal of itself.
	 */
	const onDeleteBatch = (batch) => {
		const name = importBatchName(batch);
		const confirmed = confirm(
			`Delete ${name}? Its ${batch.row_count} staged rows go with it. `
			+ 'Anything already imported stays in the budget, and the account and category mappings '
			+ 'it produced are kept.'
		);
		if(!confirmed) {
			return;
		}
		dispatch(deleteImportBatch(batch.id));
	};

	const stagedBatches = batches.filter((batch) => batch.status !== 'discarded');

	return (
		<div className='import-settings'>
			<Card>
				<h5>Import transactions</h5>
				<p className='grey-text import-lead'>
					Bring in what Monarch Money has. Nothing is written until you have mapped its accounts
					and categories and seen exactly what would happen.
				</p>
				<p className='import-rules-link'>
					<Button small flat onClick={() => dispatch(settingsTabChange(IMPORT_RULES_TAB))}>
						Import rules
					</Button>
					<span className='grey-text'>Every category rule in order, including ones by account or amount.</span>
				</p>

				<div className='import-sources'>
					<section className='import-source'>
						<h6>Upload an export</h6>
						<p className='grey-text'>
							A transactions or balances CSV from Monarch. Transactions are staged for review;
							balances fill in daily history and check an import for gaps.
						</p>
						<input
							type='file'
							accept='.csv,text/csv'
							ref={fileInput}
							style={{ display: 'none' }}
							onChange={onFileChosen}
						/>
						<div className='import-source-actions'>
							<Button
								onClick={() => fileInput.current && fileInput.current.click()}
								disabled={uploading || fetching}
							>
								{uploading ? 'Reading file…' : 'Choose a CSV file'}
							</Button>
							{uploading && <CircleLoader size='small' />}
						</div>
					</section>

					<section className='import-source'>
						<h6>Fetch from Monarch</h6>
						<p className='grey-text'>
							Sign this budget in to Monarch and its transactions are staged the same way, and
							fetched again nightly.
						</p>
						<MonarchConnection
							onFetch={onFetchFromMonarch}
							busy={uploading || fetching}
							fetching={fetching}
						/>
						{fetching && <CircleLoader size='small' />}
						{!fetching && balances.line && <p className='grey-text monarch-balances'>{balances.line}</p>}
						{!fetching && balances.error && <p className='red-text monarch-balances'>{balances.error}</p>}
					</section>
				</div>

				{!!error &&
				<div className='import-error'>
					<span>{error}</span>
					<Button small flat onClick={() => dispatch(dismissImportError())}>Dismiss</Button>
				</div>
				}

				{stagedBatches.length > 0 &&
				<div className='import-batch-list'>
					<h6>Imports</h6>
					<ul>
						{stagedBatches.map((batch) => (
							<li
								key={batch.id}
								className={'import-batch' + (isSelected(selectedBatch, batch) ? ' selected' : '')}
							>
								<button
									type='button'
									className='import-batch-name'
									aria-pressed={isSelected(selectedBatch, batch)}
									onClick={() => dispatch(selectImportBatch(batch.id))}
								>
									<span className='import-batch-title'>{importBatchName(batch)}</span>
									<span className='import-batch-meta'>
										{/** Each part kept whole, so a narrow row breaks between them and not inside a date. */}
										{batchSummary(batch).split(' · ').map((part, index) => (
											<React.Fragment key={index}>
												{index > 0 && ' · '}
												<span className='import-nowrap'>{part}</span>
											</React.Fragment>
										))}
									</span>
								</button>
								{/**
								  * An imported file cannot be deleted -- its rows are in the ledger and the batch
								  * record is the only thing left saying where they came from, so delete_batch()
								  * refuses it outright. Say so in place of the button rather than letting the
								  * click earn an error for something the screen offered.
								  */}
								{isImported(batch)
									? <span
										className='import-batch-badge'
										title='This file has been imported, so its record is kept.'
									>
										Imported
									</span>
									: <Button small flat danger onClick={() => onDeleteBatch(batch)}>Delete</Button>
								}
							</li>
						))}
					</ul>
				</div>
				}
			</Card>

			{!!selectedBatch &&
			<Card>
				<ImportAccountMapper />
			</Card>
			}

			{!!selectedBatch && selectedBatch.kind !== 'balances' &&
			<Card>
				<ImportCategoryMapper />
			</Card>
			}

			{!!selectedBatch &&
			<Card>
				{selectedBatch.kind === 'balances'
					? <ImportBalanceReport />
					: <ImportPreviewTable />
				}
			</Card>
			}
		</div>
	);
}

/** "Transactions · 3,817 rows · 2026-10-01", what tells two uploads apart besides their names. */
export function batchSummary(batch) {
	return [
		batch.kind === 'balances' ? 'Balances' : 'Transactions',
		`${Number(batch.row_count).toLocaleString('en-US')} ${Number(batch.row_count) === 1 ? 'row' : 'rows'}`,
		formatDate(batch.created_at),
	].filter(Boolean).join(' · ');
}

function isSelected(selectedBatch, batch) {
	return !!selectedBatch && selectedBatch.id === batch.id;
}

/**
 * Matches Import_Batch::STATUS_IMPORTED. Note it says the file is finished with, not that it put
 * anything in the ledger: a file that is entirely skipped or entirely duplicate imports nothing and
 * still ends up here.
 */
function isImported(batch) {
	return batch.status === 'imported';
}

function formatDate(timestamp) {
	if(!timestamp) {
		return '';
	}
	return new Date(timestamp * 1000).toISOString().slice(0, 10);
}
